import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

function PipeLines() {
  const lines = [
    { y: 15, dur: 5.0, delay: 0.0, h: 1 },
    { y: 32, dur: 3.8, delay: 0.6, h: 2 },
    { y: 48, dur: 6.2, delay: 0.2, h: 1 },
    { y: 63, dur: 4.4, delay: 1.0, h: 1 },
    { y: 79, dur: 5.6, delay: 0.4, h: 2 },
    { y: 91, dur: 3.5, delay: 0.8, h: 1 },
  ];
  return (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
      {lines.map((l, i) => (
        <motion.div key={i}
          style={{ position: 'absolute', top: `${l.y}%`, height: l.h, width: '50%', left: '-50%' }}
          animate={{
            x: ['0%', '320%'],
            backgroundColor: ['rgba(245,168,0,0.3)', 'rgba(255,255,255,0.2)', 'rgba(245,168,0,0.4)', 'rgba(245,168,0,0.3)'],
          }}
          transition={{
            x: { duration: l.dur, repeat: Infinity, ease: 'linear', delay: l.delay },
            backgroundColor: { duration: l.dur * 0.7, repeat: Infinity, ease: 'easeInOut', delay: l.delay },
          }}
        />
      ))}
    </div>
  );
}

export default function NotFound() {
  return (
    <div style={{
      minHeight: '100vh',
      background: '#080808',
      position: 'relative',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      <PipeLines />

      {/* Diagonal hazard tape band */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 1,
        clipPath: 'polygon(0 42%, 100% 35%, 100% 39%, 0 46%)',
        backgroundImage: 'repeating-linear-gradient(-45deg, #F5A800 0, #F5A800 10px, #111 10px, #111 20px)',
        opacity: 0.6,
        pointerEvents: 'none',
      }} />

      {/* Content */}
      <div style={{ position: 'relative', zIndex: 10, textAlign: 'center', padding: '2rem' }}>

        {/* Error code */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
          style={{
            fontFamily: 'var(--font-condensed)',
            fontSize: 11,
            letterSpacing: '0.35em',
            color: 'rgba(245,168,0,0.5)',
            textTransform: 'uppercase',
            marginBottom: '1rem',
          }}
        >
          Error · Route not found
        </motion.div>

        {/* 404 — giant outline text */}
        <div style={{ position: 'relative', lineHeight: 1, marginBottom: '1.5rem' }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'clamp(9rem, 30vw, 22rem)',
              lineHeight: 0.85,
              color: 'transparent',
              WebkitTextStroke: '2px rgba(245,168,0,0.25)',
              userSelect: 'none',
              letterSpacing: '-0.02em',
            }}
          >
            404
          </motion.div>
          {/* Solid 404 layered on top, slightly offset */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.12, ease: [0.16, 1, 0.3, 1] }}
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'clamp(9rem, 30vw, 22rem)',
              lineHeight: 0.85,
              color: 'var(--bce-gold)',
              userSelect: 'none',
              letterSpacing: '-0.02em',
              position: 'absolute',
              top: 6, left: 6,
              clipPath: 'polygon(0 0, 100% 0, 100% 34%, 0 41%)',
            }}
          >
            404
          </motion.div>
        </div>

        {/* "SECTOR UNAVAILABLE" */}
        <motion.div
          initial={{ opacity: 0, x: -16 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4, duration: 0.5 }}
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'clamp(1.5rem, 4vw, 3rem)',
            color: 'var(--bce-white)',
            letterSpacing: '0.1em',
            marginBottom: '0.75rem',
          }}
        >
          SECTOR UNAVAILABLE
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.55 }}
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: 14,
            color: 'var(--bce-text)',
            marginBottom: '2.5rem',
            letterSpacing: '0.02em',
          }}
        >
          The route you're looking for doesn't exist or has been moved.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
        >
          <Link to="/" className="bce-btn bce-btn-gold">
            Return to Base <ArrowRight size={15} />
          </Link>
        </motion.div>

        {/* Bottom code */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          style={{
            marginTop: '4rem',
            fontFamily: 'monospace',
            fontSize: 10,
            color: 'rgba(255,255,255,0.08)',
            letterSpacing: '0.15em',
          }}
        >
          BCE_SYS · 0x404 · ROUTE_UNRESOLVED · {new Date().toISOString().slice(0,10)}
        </motion.div>
      </div>
    </div>
  );
}
